<script lang="ts">
    import { faAngleLeft, faEdit, faPlusCircle, faSave } from '@fortawesome/free-solid-svg-icons'
    import { onMount } from 'svelte/internal'
    import { CompaniesRepository } from '../../../modules/usersApi/companies/companies.repository'
    import { CompaniesService } from '../../../modules/usersApi/companies/companies.service'
    import { Company } from '../../../modules/usersApi/companies/company'
    import type { CompanyCreateDto } from '../../../modules/usersApi/companies/dto/company-create.dto'
    import type { CompanyUpdateDto } from '../../../modules/usersApi/companies/dto/company-update.dto'
    import LoadSpinner from '../../_common/load-spinner.svelte'
    import { GridButton, GridClass, GridCol, GridColTypeEnum, GridLine } from '../../_common/z/zgrid'
    import Zgrid from '../../_common/z/zgrid.svelte'
    import Zact from '../../_common/z/zact.svelte'
    import Zframe from '../../_common/z/zframe.svelte'
    import Zbutton from '../../_common/z/zbutton.svelte'
    import Zinput from '../../_common/z/zinput.svelte'
    import ZContainer from '../../_common/z/z-container.svelte'
    import ZContainerCol from '../../_common/z/z-container-col.svelte'
    import ImagePic from '../../_common/ImagePic.svelte'
    import Content from '../../_common/content.svelte'
    const compRep = new CompaniesRepository()
    const compS = new CompaniesService(compRep)

    let show = false
    let step = 1
    let companies: Company[] = []
    let company: Company = new Company()

    const states = [
        'AC', // Acre
        'AL', // Alagoas
        'AP', // Amapá
        'AM', // Amazonas
        'BA', // Bahia
        'CE', // Ceará
        'DF', // Distrito Federal
        'ES', // Espírito Santo
        'GO', // Goiás
        'MA', // Maranhão
        'MT', // Mato Grosso
        'MS', // Mato Grosso do Sul
        'MG', // Minas Gerais
        'PA', // Pará
        'PB', // Paraíba
        'PR', // Paraná
        'PE', // Pernambuco
        'PI', // Piauí
        'RJ', // Rio de Janeiro
        'RN', // Rio Grande do Norte
        'RS', // Rio Grande do Sul
        'RO', // Rondônia
        'RR', // Roraima
        'SC', // Santa Catarina
        'SP', // São Paulo
        'SE', // Sergipe
        'TO', // Tocantins
    ]

    const timezones = compS.getTimezones()

    // opção vazia explícita: sem ela o select fica sem seleção visível enquanto a UF está vazia
    const stateOptions = [{ value: null, label: '' }, ...states.map((state) => ({ value: state, label: state }))]

    onMount(async () => {
        try {
            show = true
            await loadCompanies()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    })

    async function loadCompanies() {
        try {
            show = true
            step = 1
            companies = await compS.getAll()
            loadGrid()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    let grid: GridClass = new GridClass()
    function loadGrid() {
        // grid
        grid = new GridClass()
        // grid head
        grid.title = 'Empresas'
        grid.subtitle = ''
        grid.cols.push(new GridCol({ content: 'Nome Fantasia' }))
        grid.cols.push(new GridCol({ content: 'Razão Social' }))
        grid.cols.push(new GridCol({ content: 'Documento' }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        // grid lines
        for (const item of companies) {
            const btnEdit = new GridButton({
                callback: () => editCompany(item.companyId),
                icon: faEdit,
                label: 'Editar',
            })
            let line: GridLine = new GridLine()
            line.slots.push({ content: item?.companyName })
            line.slots.push({ content: item?.companyLegalName || '-' })
            line.slots.push({ content: item?.document || '-' })
            line.slots.push({ content: [btnEdit] })
            grid.lines.push(line)
        }
    }

    async function editCompany(companyId: string) {
        try {
            show = true
            company = new Company()
            company = await compS.getOne(companyId)
            console.log('company', company)
            step = 2
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function newCompany() {
        company = new Company()
        company.timezone = -180
        step = 2
    }

    async function saveCompany() {
        if (company.companyId) {
            updateCompany()
        } else {
            createCompany()
        }
    }

    async function updateAppTimezone() {
        try {
            await window.api.tools.sites.profiles.updateTimezone(company.timezone)
        } catch (error) {}
    }
    async function createCompany() {
        try {
            if (show) return
            show = true
            const compDto: CompanyCreateDto = {
                email: company.email,
                companyName: company.companyName,
                timezone: company.timezone,
                city: company.city,
                state: company.state,
                street: company.street,
                addressNumber: company.addressNumber,
                addressComplement: company.addressComplement,
                neighborhood: company.neighborhood,
                zipCode: company.zipCode,
                phone: company.phone,
                logo: company.logo,
                icon: company.icon,
                companyLegalName: company.companyLegalName,
                document: company.document,
            }
            company = await compS.create(compDto)
            await updateAppTimezone()
            await loadCompanies()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function updateCompany() {
        try {
            if (show) return
            show = true
            const compDto: CompanyUpdateDto = {
                email: company.email,
                companyName: company.companyName,
                timezone: company.timezone,
                city: company.city,
                state: company.state,
                street: company.street,
                addressNumber: company.addressNumber,
                addressComplement: company.addressComplement,
                neighborhood: company.neighborhood,
                zipCode: company.zipCode,
                phone: company.phone,
                logo: company.logo,
                icon: company.icon,
                companyLegalName: company.companyLegalName,
                document: company.document,
            }
            await compS.update(company.companyId, compDto)
            await updateAppTimezone()
            await loadCompanies()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function removeCompany() {
        if (!confirm('Deseja mesmo excluir esta empresa?')) {
            return
        }
        try {
            show = true
            await compS.remove(company.companyId)
            await loadCompanies()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function showCompanies() {
        step = 1
    }

    function evaluateError(error) {
        window.error(error)
    }
</script>

<Content footer={true}>
    {#if step === 1}
        <Zact>
            <div class="spacer" />
            <Zbutton color="primary" icon={faPlusCircle} label="Nova empresa" mobileHideLabel={true} on:click={newCompany} />
        </Zact>
        <Zgrid class="h-space" {grid}></Zgrid>
    {/if}
    {#if step === 2}
        <Zact class="h-space">
            <Zbutton color="secondary" icon={faAngleLeft} label="Voltar" on:click={showCompanies} />
            <div class="spacer" />
            <!-- {#if !!company.companyId}
                <Zbutton color="red" icon={faTrashAlt} label="Excluir" disabled={show} on:click={removeCompany} />
            {/if} -->
            <Zbutton color="primary" icon={faSave} label="Salvar" loading={show} disabled={show} on:click={saveCompany} />
        </Zact>

        <Zframe class="h-space" title="Dados Gerais">
            <ZContainer gapRow="8px" gapCol="8px">
                <Zinput size={4} sizeM={12} label="Nome Fantasia" type="text" bind:value={company.companyName} />
                <Zinput size={4} sizeM={12} label="Razão Social" type="text" bind:value={company.companyLegalName} />
                <Zinput size={4} sizeM={12} label="Documento (CPF / CNPJ)" type="text" bind:value={company.document} />
                <Zinput size={4} sizeM={12} label="E-Mail" type="text" bind:value={company.email} />
                <Zinput size={4} sizeM={12} label="Telefone" type="tel" bind:value={company.phone} />
                <Zinput size={4} sizeM={12} label="Fuso horário" type="select" options={timezones} bind:value={company.timezone} />
            </ZContainer>
        </Zframe>

        <Zframe class="h-space" title="Endereço">
            <ZContainer gapRow="8px" gapCol="8px">
                <Zinput size={2} sizeM={12} label="CEP" type="text" bind:value={company.zipCode} />
                <Zinput size={5} sizeM={12} label="Endereço" type="text" bind:value={company.street} />
                <Zinput size={2} sizeM={12} label="Nº" type="text" bind:value={company.addressNumber} />
                <Zinput size={3} sizeM={12} label="Complemento" type="text" bind:value={company.addressComplement} />
                <Zinput size={4} sizeM={12} label="Bairro" type="text" bind:value={company.neighborhood} />
                <Zinput size={4} sizeM={12} label="Cidade" type="text" bind:value={company.city} />
                <Zinput size={2} sizeM={12} label="UF" type="select" options={stateOptions} bind:value={company.state} />
            </ZContainer>
        </Zframe>

        <Zframe class="h-space" title="Imagens">
            <ZContainer gapRow="8px" gapCol="8px">
                <ZContainerCol size={6} sizeM={12}>
                    <ImagePic inline={true} label="Logotipo" bind:base64={company.logo} upload={true}></ImagePic>
                </ZContainerCol>
                <ZContainerCol size={6} sizeM={12}>
                    <ImagePic inline={true} label="Ícone" bind:base64={company.icon} upload={true}></ImagePic>
                </ZContainerCol>
            </ZContainer>
        </Zframe>
    {/if}
</Content>

<LoadSpinner {show} />
